import _ from './dialog.module.scss'
import React, {useCallback, useContext, useEffect, useRef, useState} from 'react'
import {$, $$$} from '../shared/class-name'
import {ArrowLeftIcon, CheckIcon, CloseIcon} from '../icons'
import {IconButton} from './icon-button'
import {Button} from './button'
import {TriSpinner} from './tri-spinner'
import {DialogCloseContext} from './dialog-context'

export const Dialog = ({
    title,
    titleBar = true,
    icon,
    children,
    message,
    hint,
    actions = [],
    positiveAction,
    negativeAction,
    footer,
    backAction,
    onClose,
    closeOnOverlay = true,
    submitting = false,
    maxWidth = 360,
    className,
    bodyClassName,
    centeredFooter = false
}) => {
    const [visible, setVisible] = useState(false)
    const [successActionKey, setSuccessActionKey] = useState(null)
    const registerClose = useContext(DialogCloseContext)
    const closeTimerRef = useRef(null)

    useEffect(() => { requestAnimationFrame(() => setVisible(true)) }, [])

    const handleClose = useCallback(() => {
        if (closeTimerRef.current != null) return
        setVisible(false)
        closeTimerRef.current = setTimeout(onClose, 200)
    }, [onClose])

    useEffect(() => registerClose?.(handleClose), [registerClose, handleClose])
    useEffect(() => () => clearTimeout(closeTimerRef.current), [])

    const handleAction = async (action) => {
        await action.onClick?.()
        if (action.close !== false) {
            if (action.positive) setSuccessActionKey(action.actionKey)
            handleClose()
        }
    }

    const hasStandardActions = positiveAction !== undefined || negativeAction !== undefined
    const standardActions = [
        {action: negativeAction === undefined ? {icon: <CloseIcon size={
            positiveAction?.iconWithTitle || actions.some(a => a.iconWithTitle) ? 14 : 16
        }/>, title: 'Cancel', color: 'light'} : negativeAction, positive: false},
        {action: positiveAction, positive: true},
    ].filter(({action}) => action).map(({action, positive}) => ({
        ...action,
        actionKey: action.key || action.label || action.title || (positive ? 'positive' : 'negative'),
        positive,
        disabled: action.disabled || submitting,
        loading: action.loading || (positive && submitting),
    }))
    const footerActions = hasStandardActions ? standardActions : actions.length ? actions : [{label: 'OK'}]
    const renderAction = (action, index) => {
        const key = action.actionKey || action.key || action.label || action.title || index
        const success = successActionKey === action.actionKey
        const label = action.label || (action.iconWithTitle ? action.title : null)
        const color = action.color || 'secondary'
        if (action.icon && !label) {
            return <IconButton
                key={key}
                color={action.color}
                size={action.size}
                title={action.title}
                disabled={action.disabled || success}
                className={action.className}
                style={action.style}
                onClick={() => handleAction(action)}
            >
                {(action.loading || success) ? (
                    <span className={_.actionIconSwap}>
                        <TriSpinner
                            size={action.spinnerSize || 24}
                            color={action.color}
                            className={$(_.actionIconSwapHidden, success)}
                        />
                        <span className={$$$(_.actionSuccessIcon, $(_.actionSuccessIconVisible, success))}>
                            <CheckIcon size={action.successIconSize || 16}/>
                        </span>
                    </span>
                ) : action.icon}
            </IconButton>
        }
        return <Button
            key={key}
            color={color}
            disabled={action.disabled}
            loading={action.loading}
            onClick={() => handleAction(action)}
            style={action.style}
        >
            {action.iconWithTitle && action.icon && <span className={_.actionButtonIcon}>{action.icon}</span>}
            {label}
        </Button>
    }

    return <div
        className={$$$(_.overlay, $(_.overlayHidden, !visible))}
        onClick={closeOnOverlay ? handleClose : undefined}
    >
        <div className={$$$(_.dialog, $(className))} style={{maxWidth}} onClick={e => e.stopPropagation()}
            role="dialog" aria-modal="true" aria-label={typeof title === 'string' ? title : undefined}>
            {titleBar ? (
                <div className={_.dialogHead}>
                    <IconButton
                        onClick={backAction?.onClick || handleClose}
                        size={'medium'}
                        color={'light'}
                        title={backAction?.title || 'Close'}
                    >
                        {backAction ? <ArrowLeftIcon size={18}/> : <CloseIcon size={14}/>}
                    </IconButton>
                    <span className={_.dialogTitle}>{title}</span>
                    {icon && <span className={_.dialogHeadIcon}>{icon}</span>}
                </div>
            ) : (
                (icon || title) && (
                    <div className={_.dialogIntro}>
                        {icon && <div className={_.dialogIntroIcon}>{icon}</div>}
                        {title && <div className={_.dialogIntroTitle}>{title}</div>}
                    </div>
                )
            )}
            <div className={$$$(_.dialogBody, $(bodyClassName))}>
                {message && <div className={_.message}>{message}</div>}
                {typeof children === 'function' ? children({close: handleClose}) : children}
                {hint && <div className={_.hint}>{hint}</div>}
            </div>
            {footer !== undefined ? footer : (
                <div className={$$$(_.dialogFoot, $(_.centeredFooter, centeredFooter))}>
                    {footerActions.map(renderAction)}
                </div>
            )}
        </div>
    </div>
}
